<template>
    <grid
        ref="grid"
        :column-defs="columnDefs"
        :fetch-callback="fetchCallback"
        :maxBlocksInCache="null"
    >
        <date-range-picker
            :range-options="predefinedRanges"
            :value="dateRange"
            :date-format="userUnicodeDateFormat"
            @input="handleDaterangeChange"
        ></date-range-picker>
        <b-button-group aria-label="Export table data">
            <responsive-button
                :icon="['fas', 'file-excel']"
                :href="exportExcelHref"
                @click="generatingExcelReportSuccessToast"
                variant="primary"
            ></responsive-button>
        </b-button-group>
        <responsive-button
            :icon="['fas', 'arrows-rotate']"
            variant="primary"
            @click="refresh"
            >Refresh</responsive-button
        >
        <modal :id="inebriationWarningModalID" title="Notice" ok-only>
            Just a friendly reminder as we move through the New Year's period.
            Damage due to inebriation is NOT considered accidental damage.
            Claims are reviewed with more scrutiny during this period to assure
            the terms of service are being followed
        </modal>
    </grid>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import subYears from 'date-fns/subYears';
import API from '../../../api';
import {
    DAMAGE_PROTECTION_INVOICE_INSURANCE_EXCEL_ENDPOINT,
    DAMAGE_PROTECTION_INVOICE_INSURANCE_GRID_ENDPOINT,
} from '../../../endpoints';
import { escapeHTML } from '../../../utils/strings';
import DateRangePicker, {
    ALL_DATES,
    CUSTOM_RANGE,
    LAST_MONTH,
    LAST_YEAR,
    THIS_MONTH,
    THIS_YEAR,
    TODAY,
    YESTERDAY,
} from '../../generic/date-range-picker/date-range-picker.vue';
import Grid from '../../generic/grid/grid.vue';
import Modal from '../../generic/modal/modal.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import PolicyCellRenderer from './policy-cell-renderer.vue';

export default {
    name: 'DamageProtectionIssuances',
    components: {
        DateRangePicker,
        Grid,
        Modal,
        ResponsiveButton,
        actionsCellRenderer: ActionsCellRenderer,
        policyCellRenderer: PolicyCellRenderer,
    },
    computed: {
        ...mapGetters('account', ['inebriationWarningShowed']),
        ...mapGetters('header', [
            'userCurrencySymbol',
            'userUnicodeDateFormat',
        ]),
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                return API.get(
                    DAMAGE_PROTECTION_INVOICE_INSURANCE_GRID_ENDPOINT(),
                    {
                        params: {
                            offset,
                            limit,
                            sort,
                            desc,
                            search,
                            from_date: this.fromDateRequest,
                            to_date: this.toDateRequest,
                        },
                    }
                );
            };
        },
        exportExcelHref() {
            return (
                `${DAMAGE_PROTECTION_INVOICE_INSURANCE_EXCEL_ENDPOINT()}?offset=${
                    this.offset
                }&limit=${this.limit}` +
                `&sort=${this.sort}&desc=${this.desc}&from_date=${this.fromDateRequest}&to_date=${this.toDateRequest}` +
                `&search=${this.search}`
            );
        },
        fromDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        toDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
    },
    data() {
        return {
            columnDefs: [
                {
                    field: 'created',
                    headerName: 'Created',
                    type: 'dateColumn',
                    sort: 'desc',
                },
                {
                    field: 'product_type',
                    headerName: 'Type',
                    sortable: false,
                },
                {
                    field: 'invoice_number',
                    headerName: 'Invoice ID',
                    cellRenderer: ({ data }) =>
                        `<a target="_self" href="${data.invoice_link}">${data.invoice_number}</a>`,
                },
                {
                    field: 'properties',
                    headerName: 'Property',
                    autoHeight: true,
                    minWidth: 320,
                    sortable: false,
                    cellRenderer: ({ data }) =>
                        data &&
                        data.properties
                            .map((property) => escapeHTML(property.name))
                            .join('<br />'),
                },
                {
                    field: 'external_policy_id',
                    headerName: 'Policy ID',
                    cellRenderer: 'policyCellRenderer',
                },
                {
                    field: 'reservation.user.full_name',
                    headerName: 'Guest Name',
                },
                {
                    field: 'from_date',
                    headerName: 'Check-In',
                    type: 'dateColumn',
                },
                {
                    field: 'to_date',
                    headerName: 'Check-Out',
                    type: 'dateColumn',
                },
                {
                    field: 'wholesale_cost',
                    headerName: 'Wholesale',
                    cellRenderer: ({ value }) => {
                        if (!isNaN(value)) {
                            const number = Number(value).toFixed(2);
                            return number >= 0
                                ? `${this.userCurrencySymbol}${number}`
                                : `<span class="text-warning">(${
                                      this.userCurrencySymbol
                                  }${-number})</span>`;
                        }
                    },
                },
                {
                    headerName: '',
                    maxWidth: 145,
                    minWidth: 145,
                    resizable: false,
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'actionsCellRenderer',
                },
            ],
            dateRange: {
                name: CUSTOM_RANGE,
                from: subYears(new Date(), 1),
                to: new Date(),
            },
            dateRequestFormat: 'yyyy-MM-dd',
            predefinedRanges: [
                TODAY,
                YESTERDAY,
                THIS_MONTH,
                LAST_MONTH,
                THIS_YEAR,
                LAST_YEAR,
                ALL_DATES,
            ],
            offset: 0,
            limit: 20,
            sort: 'id',
            desc: false,
            search: '',
            inebriationWarningModalID:
                'damage-protection-issuances-notice-modal',
        };
    },
    mounted() {
        const today = new Date();
        if (
            (!this.inebriationWarningShowed &&
                today.getMonth() == 11 &&
                today.getDate() > 25) ||
            (today.getMonth() == 0 && today.getDate() < 11)
        ) {
            this.$bvModal.show(this.inebriationWarningModalID);
            this.$store.commit('account/setInebriationWarningShowed', true);
        }
    },
    methods: {
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.refresh();
        },
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
    },
};
</script>